import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Link } from '@mui/material';
import { TextField, Autocomplete, Button } from '@totalsoft/rocket-ui';
import { RightsContext } from 'providers/RightsContext';
import { Prompt } from 'react-router-dom';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const FolderGeneralInfoComponent = ({
  t,
  generalInfo,
  onGeneralInfoChange,
  onSave,
  savingGeneralInfo,
  isDirty,
  validation,
  isNewFolder,
  accountManagersOptions,
  responsiblesOptions,
  partnersOptions,
  folderTypes,
  openLinkedDocument,
  onCalculateApproval,
  isFacilityApproval
}) => {
  const { readOnlyFull, isImage, folderCalculateApproval, folderGeneralInfoWrite } = useContext(RightsContext);

  return (
    <>
      <Prompt when={isDirty} message={() => t('LeavingWithoutSaving')} />
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <TextField
            disabled
            fullWidth
            name='folderClientName'
            value={generalInfo.client.clientName || ''}
            label={t('Folder.GeneralData.GeneralInfo.Client')}
          />
        </Grid>
        {isNewFolder ? (
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              name='folderClientUniqueId'
              value={generalInfo.client.clientId}
              onChange={value => onGeneralInfoChange(value, 'client')}
              label={t('Folder.GeneralData.GeneralInfo.UniqueId')}
              valueKey='clientId'
              labelKey='uniqueId'
              options={partnersOptions}
              error={!isValid(validation.clientId)}
              helperText={getErrors(validation.clientId)}
            />
          </Grid>
        ) : (
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              fullWidth
              name='folderClientUniqueId'
              value={generalInfo.client.uniqueId || ''}
              onChange={onGeneralInfoChange}
              label={t('Folder.GeneralData.GeneralInfo.UniqueId')}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            name='folderResponsible'
            value={generalInfo.responsibleId}
            valueKey='responsibleId'
            labelKey='responsibleName'
            label={t('Folder.GeneralData.GeneralInfo.Responsible')}
            options={responsiblesOptions}
            onChange={value => {
              onGeneralInfoChange({
                responsibleId: value,
                responsibleName: responsiblesOptions.find(x => x.responsibleId === value).responsibleName
              });
            }}
            disabled={readOnlyFull || !folderGeneralInfoWrite}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            name='folderRiskResponsible'
            value={generalInfo}
            valueKey='accountManagerId'
            labelKey='accountManagerName'
            label={t('Folder.GeneralData.GeneralInfo.RiskResponsible')}
            options={accountManagersOptions}
            onChange={onGeneralInfoChange}
            disabled={readOnlyFull || !folderGeneralInfoWrite}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            disabled={readOnlyFull || !folderGeneralInfoWrite}
            fullWidth
            value={generalInfo.folderTypeId}
            valueKey='offerTypeId'
            labelKey='offerTypeName'
            label={t('Folder.GeneralData.GeneralInfo.FolderType')}
            options={folderTypes}
            onChange={value => {
              onGeneralInfoChange({ folderTypeId: value, folderType: folderTypes.find(x => x.offerTypeId === value).offerTypeName });
            }}
            isClearable={false}
            simpleValue={true}
          />
        </Grid>
        {isFacilityApproval && generalInfo.renewedFacilityId && (
          <Grid item xs={12} md={6}>
            <TextField
              id={'folderGeneralInfo_RenewFacilityNo'}
              disabled={true}
              fullWidth
              label={t('Folder.GeneralData.GeneralInfo.RenewFacilityNo') + ' '}
              startAdornment={
                <Link
                  style={{ cursor: 'pointer' }}
                  onClick={openLinkedDocument(generalInfo.renewedFacilityId, generalInfo.renewedFacilitySiteId)}
                >
                  {generalInfo.renewedFacilityNumber}
                </Link>
              }
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            id={'folderGeneralInfo_PartnerGroup'}
            disabled={true}
            fullWidth
            label={t('Folder.GeneralData.GeneralInfo.PartnerGroup')}
            value={generalInfo.partnerGroup || ''}
          />
        </Grid>
        {!isImage && (
          <Grid item xs={12} mt={1}>
            <Button
              onClick={onSave}
              disabled={savingGeneralInfo || readOnlyFull || !folderGeneralInfoWrite || !isDirty || !(validation |> isValid)}
            >
              {savingGeneralInfo ? t('Saving') + '...' : t('Save')}
            </Button>
          </Grid>
        )}
        {!isImage && (
          <Grid item xs={2} mt={1}>
            <Button
              color='primary'
              variant='contained'
              onClick={onCalculateApproval}
              id={'folderGeneralInfo_calculateApproval'}
              disabled={!generalInfo.canCalculateApproval || !folderCalculateApproval}
            >
              {t('Folder.GeneralData.GeneralInfo.CalculateApproval')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};

FolderGeneralInfoComponent.propTypes = {
  t: PropTypes.func.isRequired,
  generalInfo: PropTypes.object.isRequired,
  onGeneralInfoChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  savingGeneralInfo: PropTypes.bool,
  isDirty: PropTypes.bool.isRequired,
  validation: PropTypes.object,
  isNewFolder: PropTypes.bool,
  accountManagersOptions: PropTypes.array.isRequired,
  responsiblesOptions: PropTypes.array.isRequired,
  partnersOptions: PropTypes.array,
  folderTypes: PropTypes.array,
  onCalculateApproval: PropTypes.func,
  openLinkedDocument: PropTypes.func,
  isFacilityApproval: PropTypes.bool
};

export default withTranslation()(FolderGeneralInfoComponent);
